import React, { useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import sjpic from '../../assets/images/jesuits.jpg';
import inigopic from '../../assets/images/igni.jpg';
import companianpic from '../../assets/images/first-companians.png';
import { Sparkles, Compass, Globe } from 'lucide-react';

export default function SocityHistory() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-5" style={{ background: '#f8fafc', minHeight: '85vh' }}>
      <Helmet>
        <title>History of the Society of Jesus - Vidyajyoti College of Theology</title>
        <meta name="description" content="Learn about Saint Ignatius of Loyola, the First Companions, and the global mission of the Society of Jesus (Jesuits)." />
        <meta name="keywords" content="Society of Jesus, Jesuits, Ignatius Loyola, Ignatian Spirituality, Jesuit History, AMDG" />
        <link rel="canonical" href="/about/sjinfo" />
      </Helmet>

      <div className="container">
        {/* Page Header */}
        <div className="text-center mx-auto pb-5" style={{ maxWidth: '800px' }}>
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(198, 146, 20, 0.12)', color: '#c69214', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(198, 146, 20, 0.25)' }}>
            <Sparkles size={14} /> Jesuit Heritage
          </div>
          <h1 className="display-6 fw-bold mb-3" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
            The Society of Jesus
          </h1>
          <p className="lead text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
            Founded in 1540 by Saint Ignatius of Loyola and his companions, the Society of Jesus is dedicated to the greater glory of God (<em>Ad Majorem Dei Gloriam</em>) and the holistic service of faith and promotion of justice worldwide.
          </p>
        </div>

        {/* Section 1: Who We Are & Founder */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-5">
          <div className="row align-items-center g-4">
            <div className="col-lg-7">
              <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: '#1a2b56', color: '#fff' }}>Foundations</span>
              <h3 className="fw-bold mb-3" style={{ color: '#1a2b56' }}>Saint Ignatius of Loyola &amp; The First Companions</h3>
              <p className="text-muted" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                The Society of Jesus was founded in 1540 by Saint Ignatius of Loyola, a soldier-turned-mystic, alongside Francis Xavier, Peter Faber, and seven university companions in Paris. The Jesuit mission is one of universal reconciliation: reconciling women and men with God, with themselves, with each other, and with God&rsquo;s creation.
              </p>
              <p className="text-muted mb-0" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                In the vision of St. Ignatius, Jesuits seek to &ldquo;find God in all things.&rdquo; We dedicate ourselves wholeheartedly to the good of all humanity, collaborating joyfully with lay persons, church partners, and seekers of goodwill.
              </p>
            </div>
            <div className="col-lg-5 text-center">
              <div className="rounded-4 shadow-sm p-2 p-md-3 bg-light d-flex align-items-center justify-content-center" style={{ minHeight: '260px' }}>
                <img 
                  src={inigopic} 
                  alt="Saint Ignatius of Loyola" 
                  className="img-fluid rounded-3" 
                  style={{ 
                    maxHeight: '300px', 
                    maxWidth: '100%', 
                    width: 'auto', 
                    height: 'auto', 
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto' 
                  }} 
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Ignatian Spirituality & Global Mission */}
        <div className="row g-4 mb-5">
          <div className="col-lg-6">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white d-flex flex-column justify-content-between">
              <div>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="p-3 rounded-circle" style={{ background: 'rgba(121, 3, 29, 0.08)', color: '#79031d' }}>
                    <Compass size={26} />
                  </div>
                  <div>
                    <h4 className="fw-bold mb-0" style={{ color: '#79031d' }}>Ignatian Spirituality</h4>
                    <small className="text-muted">Contemplatives in Action</small>
                  </div>
                </div>
                <p className="text-muted" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                  Jesuits draw upon the rich tradition of the <em>Spiritual Exercises</em> and reflective discernment. In retreat centers, universities, parishes, and borderlands, we accompany individuals to discover God’s dynamic presence in daily life and become &ldquo;contemplatives in action&rdquo; for global peace, reconciliation, and ecological justice.
                </p>
              </div>
              <div className="rounded-4 shadow-sm mt-4 p-2 bg-light d-flex align-items-center justify-content-center" style={{ minHeight: '260px' }}>
                <img 
                  src={companianpic} 
                  alt="The First Companions of the Society of Jesus" 
                  className="img-fluid rounded-3" 
                  style={{ 
                    maxHeight: '280px', 
                    maxWidth: '100%', 
                    width: 'auto', 
                    height: 'auto', 
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto' 
                  }} 
                />
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white d-flex flex-column justify-content-between">
              <div>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="p-3 rounded-circle" style={{ background: 'rgba(26, 43, 86, 0.08)', color: '#1a2b56' }}>
                    <Globe size={26} />
                  </div>
                  <div>
                    <h4 className="fw-bold mb-0" style={{ color: '#1a2b56' }}>The Jesuits Today</h4>
                    <small className="text-muted">Global Presence in 100+ Nations</small>
                  </div>
                </div>
                <p className="text-muted" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                  With over 15,000 priests, brothers, and scholastics worldwide, the Society of Jesus is the largest religious order in the Catholic Church. As pastors, educators, scientists, social activists, and theologians, Jesuits care for the whole person (<em>cura personalis</em>), forming men and women for and with others.
                </p>
              </div>
              <div className="rounded-4 shadow-sm mt-4 p-2 bg-light d-flex align-items-center justify-content-center" style={{ minHeight: '260px' }}>
                <img 
                  src={sjpic} 
                  alt="Jesuit community in mission" 
                  className="img-fluid rounded-3" 
                  style={{ 
                    maxHeight: '280px', 
                    maxWidth: '100%', 
                    width: 'auto', 
                    height: 'auto', 
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto' 
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
